import { useTranslation } from "react-i18next";
import { DesignTokens, FormItem, FormWrapper, Select } from "melco-ui";

const { colors } = DesignTokens;

type SelectSummitAddOnProps = {
    onChange: (value: string) => void;
    value?: string;
    onClear?: (value?: any) => void;
    /** Shown directly below the select when true — a user is selected but
     * no add-on has been chosen yet, so machines can't be fetched. */
    showRequiredError?: boolean;
};

// The two add-ons currently supported by the Summit workflow.
// NOTE: if a new add-on is added, extend this list AND add a matching
// translation key at `summit.form.addon.options.<key>`.
const ADDON_KEYS = ["eod_connect"] as const;

export const SelectSummitAddOn: React.FC<SelectSummitAddOnProps> = ({
    value,
    onChange,
    onClear,
    showRequiredError,
}) => {
    const { t } = useTranslation();

    return (
        <FormWrapper layout="vertical">
            <FormItem label={t("summit.form.addon.label")}>
                <Select
                    options={ADDON_KEYS.map((addon) => ({
                        label: t(`summit.form.addon.options.${addon}`),
                        value: addon,
                    }))}
                    value={value}
                    onChange={onChange}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                    onClear={onClear}
                />
                {/* WHAT: guidance shown directly under the field, always.
                    WHY two variants instead of one: in the neutral case
                    (no user selected yet, or a user selected but no error
                    to flag) this is just a plain hint telling the rep what
                    the field is for. Once a user IS selected but no add-on
                    has been chosen, the exact same slot switches to the red
                    required-error copy — same position, so the rep's eye is
                    already on the right spot when the error appears, rather
                    than the error showing up elsewhere (e.g. the machines
                    panel) after they've moved on. */}
                <div
                    style={{
                        color: showRequiredError ? colors.main.negative : colors.gray.middle,
                        fontSize: 12,
                        marginTop: 4,
                    }}
                >
                    {showRequiredError
                        ? t("summit.form.addon.required_error", {
                            defaultValue: "Please select an add-on to view this user's machines.",
                        })
                        : t("summit.form.addon.hint", {
                            defaultValue: "Select an add-on above to load this user's machines.",
                        })}
                </div>
            </FormItem>
        </FormWrapper>
    );
};
